import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';

import AddressInput from 'components/field/AddressInput';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import * as F from 'components/form/FormLayout.styled';
import Alert from 'components/global/Alert';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';

import { openAxios } from 'services/axios';
import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import InfoLabel from './component/InfoLabel';
import * as S from './styled/AddressVerification1.styled';

/**
 * @author Burak Ak
 */

export const addressVerification1DefaultValues = [
	{
		type: 'color',
		value: '#f5f5f5',
		name: 'pageBackgroundColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'navigationBackgroundColor'
	},
	{
		type: 'color',
		value: '#1bc487',
		name: 'saveButtonColor'
	},
	{
		type: 'color',
		value: '#3F51B5',
		name: 'getLocationButtonColor'
	},
	{
		type: 'color',
		value: '#ffffff',
		name: 'formBackgroundColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'formTextColor'
	},
	{
		type: 'color',
		value: '#ffffff',
		name: 'infoBackgroundColor'
	},
	{
		type: 'color',
		value: '#535353',
		name: 'infoTextColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'headerTextColor'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showOrderNumber'
	},
	{
		type: 'boolean',
		value: true,
		name: 'adressDisabled'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showCustomerAddress'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showDeliveryNote'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnBottom'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnTop'
	}
];

const AddressVerification1 = (props) => {
	const schema = useMemo(
		() =>
			yup.object().shape({
				address: yup.object().shape({
					text: yup.string().nullable().required(),
					countryName: yup.string().nullable(),
					stateName: yup.string().nullable(),
					cityName: yup.string().nullable(),
					districtName: yup.string().nullable(),
					zipCode: yup.string().nullable(),
					lat: yup.number().min(-90).max(90).nullable(),
					lng: yup.number().min(-180).max(180).nullable()
				}),
				deliveryNote: yup.string().nullable()
			}),
		[]
	);

	const { orderNumber, useForAllAddress, address, deliveryNote, propJson } = props;

	const [memoizedAddress, setMemoizedAddress] = useState(address?.text);
	const [loading, setLoading] = useState(null);

	const { showError, showSuccess } = useAlert();

	const formMethods = useForm({
		resolver: yupResolver(schema)
	});
	const {
		handleSubmit,
		reset,
		getValues,
		watch,
		formState: { isSubmitSuccessful }
	} = formMethods;

	const afterConfirmation = async (data) => {
		setLoading(true);
		const derivedAddreess = { ...data.address };
		try {
			const response = await openAxios.post(OPEN.SMS.UPDATE_ADDRESS_VERIFICATION_SMS_DETAILS, {
				otoOrderID: props.otoOrderID,
				address: {
					...derivedAddreess,
					postCode: derivedAddreess.zipCode
				},
				deliveryNote: data.deliveryNote,
				useForAllAddress: data.useForAllAddress
			});

			if (response.status === 200) {
				showSuccess();
				setMemoizedAddress(data?.address?.text);
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			showError({
				message: error?.response?.data?.code || error?.response?.data?.message
			});
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	};

	const onSubmit = async (data) => {
		NiceModal.show(ConfirmationModal, {
			title: 'confirmDeliveryLocation',
			subTitle: (
				<Trans
					i18nKey="confirmDeliveryLocationSubTitle"
					values={{
						address: getValues('address.text')
					}}
				/>
			),
			rejectLabel: 'cancel',
			confirmLabel: 'letsProceed',
			lottieName: 'info-animated-icon',
			yesAction: () => afterConfirmation(data)
		});
	};

	// when sms layout fetched we are filling form
	useEffect(() => {
		reset({
			address: {
				text: address?.text,
				lat: address?.latitude,
				lng: address?.longitude,
				zipCode: address?.postCode,
				countryName: address?.countryName,
				stateName: address?.stateName,
				cityName: address?.cityName,
				districtName: address?.districtName
			},
			useForAllAddress,
			deliveryNote
		});
	}, [address, deliveryNote, reset, useForAllAddress]);

	const defaultColSpan = {
		md: 6
	};

	return (
		<FormProvider {...formMethods}>
			<div style={{ maxWidth: '106rem', width: '100%', margin: 'auto' }}>
				{propJson?.showOrderNumber || propJson?.showCustomerAddress ? (
					<S.Card formBackgroundColor={propJson?.formBackgroundColor}>
						{propJson?.showOrderNumber && (
							<InfoLabel
								rowMode
								label="orderNumber"
								value={orderNumber}
								labelFontWeight="bold"
								labelVariant="p1"
								valueFontWeight="500"
								labelColor={propJson?.headerTextColor}
								valueColor={propJson?.formTextColor}
							/>
						)}

						{propJson?.showOrderNumber && propJson?.showCustomerAddress && <F.Line />}

						{propJson?.showCustomerAddress && (
							<InfoLabel
								rowMode
								label="currentAddress"
								iconColor={propJson?.headerTextColor}
								value={isSubmitSuccessful ? memoizedAddress : address?.text}
								labelFontWeight="bold"
								labelVariant="p1"
								labelColor={propJson?.headerTextColor}
								valueFontWeight="500"
								valueColor={propJson?.formTextColor}
							/>
						)}
					</S.Card>
				) : (
					''
				)}

				<S.TransparentCard>
					<Alert variant="warning" showIcon style={{ backgroundColor: propJson?.infoBackgroundColor }}>
						<Typography fontWeight="500" color={propJson?.infoTextColor}>
							pleaseDragInfo
						</Typography>
					</Alert>
				</S.TransparentCard>

				<S.FormWrapper formBackgroundColor={propJson?.formBackgroundColor} color={propJson?.formTextColor}>
					<Form formMethods={formMethods}>
						<AddressInput
							showButton={false}
							name={'address'}
							disabled={propJson?.adressDisabled}
							restrictions={
								address && {
									country: {
										isNotDifferent: true,
										code: address?.countryName
									}
								}
							}
							withMap={true}
							labelColor={propJson?.formTextColor}
						/>
						<S.FormItemsWrapper>
							<FormItem label="country" colSpan={defaultColSpan} color={propJson?.formTextColor} disabled>
								<StringInput
									value={watch('address.countryName')}
									disabled
									color={propJson?.formTextColor}
								/>
							</FormItem>
							<FormItem label="state" colSpan={defaultColSpan} color={propJson?.formTextColor}>
								<StringInput value={watch('address.stateName')} disabled />
							</FormItem>
							<FormItem label="city" colSpan={defaultColSpan} color={propJson?.formTextColor}>
								<StringInput value={watch('address.cityName')} disabled />
							</FormItem>
							<FormItem label="district" colSpan={defaultColSpan} color={propJson?.formTextColor}>
								<StringInput value={watch('address.districtName')} disabled />
							</FormItem>

							<FormItem label="zipCode" colSpan={defaultColSpan} color={propJson?.formTextColor}>
								<StringInput value={watch('address.zipCode')} disabled />
							</FormItem>
						</S.FormItemsWrapper>
					</Form>
				</S.FormWrapper>

				{propJson?.showDeliveryNote && (
					<S.TransparentCard>
						<TextArea
							placeholder="enterYourNote"
							name="deliveryNote"
							label="deliveryNote"
							style={{ backgroundColor: propJson?.formBackgroundColor, color: propJson?.formTextColor }}
							disabled={isSubmitSuccessful}
						/>
					</S.TransparentCard>
				)}

				<S.TransparentCard>
					<FormItem label="useThisAddressForOrders" direction="row-reverse" color={propJson?.formTextColor}>
						<SwitchInput name="useForAllAddress" className="mb-2" />
					</FormItem>
				</S.TransparentCard>
				<S.TransparentCard style={{ justifyContent: 'center', display: 'flex' }}>
					<S.SaveButtonDesign saveButtonColor={propJson?.saveButtonColor}>
						<Button
							onClick={handleSubmit(onSubmit, (errors) => {
								AppLogger.log(errors);
							})}
							size="large"
							label="confirmDeliveryLocation"
							loading={loading}
							disabled={isSubmitSuccessful}
						/>
					</S.SaveButtonDesign>
				</S.TransparentCard>
			</div>
		</FormProvider>
	);
};

export default AddressVerification1;
